import { Grid, Box } from '@material-ui/core';
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '../../../../../hooks/query/useQuery';
import { useTrans } from '../../../../../services/i18n';
import { SpacingContainer } from '../../../../common/Containers';
import SectionScrollable from '../../../../common/Containers/SectionScrollable';
import Loader from '../../../../common/Loader';
import QuickViewLayout from '../../../QuickViewLayout';
import connect from '../connect';
import ActionsPanel from './ActionsPanel';
import NotificationSmallCard from './Notification/NotificationSmallCard';
import QickViewCard from '../../../../generics/Cards/QickViewCard';
import { isItemTypeEnabled, useNotifications } from '../hooks/useNotifications';
import { useLiveSync } from '../hooks/useLiveSync';
import { Button } from '../../../../common/Buttons';
import Typography from '../../../../common/Typography';
import NothingFound from './NothingFound';
import { useTheme } from '@material-ui/core/styles';
import { ROUTE_PROFILE } from '../../../../../constants';
import { IconSMSNotification } from '../../../../svg';
import useBooleanHandlers from '../../../../../hooks/useBooleanHandlers';
import useMinuteTick from '../../../../../hooks/useMinuteTick';
import EmailNotificationModal from '../../../../../containers/Profile/EmailNotificationSettingsProfile/components/EmailNotificationModal';
import { useIsMobile } from '../../../../../hooks/useIsMobile';
import useStyles from './styles';

function getEmptyTitle({ isArchive, onlyCrmEnabled, nothingFoundTitle }) {
  if (isArchive) return 'YourTrashIsEmpty';
  if (onlyCrmEnabled) return 'no-crm-alerts';
  return nothingFoundTitle;
}

function Notifications(props) {
  const theme = useTheme();
  const styles = useStyles();
  const isMobile = useIsMobile('sm');
  const {
    scope,
    notifications,
    onClose,
    onChangePage,
    onCheckItem,
    onMarkAsReadSingle,
    onRefreshList,
    onSyncList,
    activeOrganizationId,
    isArchive,
    authUser,
    updateEmailNotificationSettings,
  } = props;
  const { query } = useQuery();
  const { trans } = useTrans();
  const scrollRef = useRef(null);
  const now = useMinuteTick();
  const {
    items,
    pending,
    checkedItems,
    hasItems,
    selectedIndex,
    openSelected,
    onLoadMore,
    notificationFilters,
    setNotificationFilters,
    resetNotificationFilters,
    onlyCrmEnabled,
    disableLoadMore,
    nothingFoundTitle,
    nothingFoundSubtitle,
  } = useNotifications(notifications, onMarkAsReadSingle, onChangePage, onRefreshList);
  const { newCount, scrollToTop, onScroll } = useLiveSync({
    isArchive,
    activeOrganizationId,
    pending,
    items: notifications.items,
    scrollRef,
    onSyncList,
    isItemEnabled: (item) => isItemTypeEnabled(notificationFilters, item),
  });

  // Open and close the email notification modal
  const [openModal, onOpenModal, onCloseModal] = useBooleanHandlers();

  const emptyTitle = getEmptyTitle({ isArchive, onlyCrmEnabled, nothingFoundTitle });
  const hasVisibleItems = items.length > 0 && Boolean(hasItems);
  const showNothingFound = !hasVisibleItems && !pending;

  const getTitle = () => {
    if (isMobile) {
      return (
        <>
          <Grid container sm={12} xs={12}>
            <Grid
              sm={5}
              xs={5}
              style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', paddingLeft: '0.5rem' }}
            >
              {trans('Notifications')}
            </Grid>
            <Grid sm={7} xs={7} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Box>
                <Button style={{ color: theme.palette.primary.main }} variant="link" onClick={onOpenModal}>
                  <Box display="flex" justifyContent="center" alignItems="center" gridGap={'.5rem'}>
                    <IconSMSNotification htmlColor={theme.palette.primary.main} />
                    <Typography weight="400" variant="body1">
                      {trans('settings')}
                    </Typography>
                  </Box>
                </Button>
              </Box>
            </Grid>
          </Grid>
        </>
      );
    } else {
      return <>{trans('Notifications')}</>;
    }
  };

  return (
    <QuickViewLayout
      title={getTitle()}
      onBackClick={onClose}
      actions={
        <ActionsPanel
          scope={scope}
          notificationFilters={notificationFilters}
          setNotificationFilters={setNotificationFilters}
        />
      }
      search={
        !isMobile && (
          <Box display="flex" justifyContent="end" alignItems="center" height="100%" padding="0 1rem">
            <Button style={{ color: theme.palette.primary.main }} variant="link" onClick={onOpenModal}>
              <Box display="flex" justifyContent="center" alignItems="center" gridGap={'.5rem'}>
                <IconSMSNotification htmlColor={theme.palette.primary.main} />
                <Typography weight="400" variant="body1">
                  {trans('email-notification-settings')}
                </Typography>
              </Box>
            </Button>
          </Box>
        )
      }
    >
      <Grid container className="forwardScroll" height="100%">
        <Grid item md={openSelected ? 5 : 12} sm={12} xs={12} className="forwardScroll" height="100%">
          <SectionScrollable
            bgColor="paperSecondary"
            dividedFixed
            style={{ display: 'flex', height: '100%', flexDirection: 'column' }}
            scrollProps={{
              ref: scrollRef,
              onScroll,
              'data-testid': 'notifications-scrollable',
              style: { display: 'flex', flexDirection: 'column', height: '100%', overflowAnchor: 'none' },
            }}
          >
            <Loader open={pending} />

            {newCount > 0 && (
              <div className={styles.newPillAnchor} data-testid="new-alerts-pill-anchor">
                <button type="button" className={styles.newPill} onClick={scrollToTop}>
                  {trans('new-alerts-pill', { count: newCount })}
                </button>
              </div>
            )}

            <QickViewCard chosen={true} />
            {hasVisibleItems && (
              <Box paddingTop="1rem">
                {items.map(({ label, items: groupItems }) => (
                  <Box padding="0rem 1rem 1rem 1rem" key={label}>
                    <Box paddingBottom="0.625rem">
                      <Typography variant="body2" weight={600} color="label">
                        {trans(label)}
                      </Typography>
                    </Box>
                    <ul className={styles.groupList}>
                      {groupItems.map((item, index) => (
                        <li key={item.id} data-card-id={item.id} className={styles.cardItem}>
                          <NotificationSmallCard
                            item={item}
                            checked={checkedItems.includes(item.id)}
                            onCheckItem={onCheckItem}
                            onMarkAsRead={onMarkAsReadSingle}
                            now={now}
                            chosen={selectedIndex === index}
                            data-index={index}
                            openSelected={openSelected}
                          />
                        </li>
                      ))}
                    </ul>
                  </Box>
                ))}
              </Box>
            )}
            {showNothingFound && (
              <SpacingContainer px={2} py={2} style={{ flex: 1 }}>
                <NothingFound
                  title={trans(emptyTitle)}
                  description={trans(!query.arch ? nothingFoundSubtitle : 'AllTrashedNotificationAppear')}
                  canClearFilters={hasItems}
                  onClearFilters={resetNotificationFilters}
                />
              </SpacingContainer>
            )}

            {!isArchive && (
              <SpacingContainer style={{ padding: '1rem', paddingTop: !items.length ? 0 : '1rem' }}>
                <Button
                  fullWidth
                  variant="text"
                  disabled={disableLoadMore}
                  onClick={onLoadMore}
                  style={{ backgroundColor: 'white', padding: '1.5rem 0', borderRadius: '0.75rem' }}
                >
                  <Typography color={disableLoadMore ? 'hint' : 'primary'} variant="body1" weight={500}>
                    {trans('load-previous-7-days')}
                  </Typography>
                </Button>
              </SpacingContainer>
            )}
          </SectionScrollable>
        </Grid>
      </Grid>
      <EmailNotificationModal
        user={authUser}
        onUpdate={updateEmailNotificationSettings}
        open={openModal}
        onClose={onCloseModal}
      />
    </QuickViewLayout>
  );
}

Notifications.propTypes = {
  scope: PropTypes.string,
  notifications: PropTypes.shape({
    items: PropTypes.array,
    count: PropTypes.number,
  }),
  onClose: PropTypes.func,
  onChangePage: PropTypes.func,
  onCheckItem: PropTypes.func,
  onMarkAsReadSingle: PropTypes.func,
  onRefreshList: PropTypes.func,
  onSyncList: PropTypes.func,
  activeOrganizationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isArchive: PropTypes.bool,
  authUser: PropTypes.object,
  updateEmailNotificationSettings: PropTypes.func,
};

export default connect(Notifications);
